import { useState, type FormEvent } from 'react'
import { Box, Button, Stack } from '@n26/nxd'
import { useTranslation } from 'react-i18next'
import { StepHeader } from '@/features/transfer/components/StepHeader'
import { AmountField } from '@/features/transfer/components/AmountField'
import { ReferenceField } from '@/features/transfer/components/ReferenceField'
import { TransferSummaryCard } from '@/features/transfer/components/TransferSummaryCard'
import {
  validateAmount,
  validateReference,
} from '@/features/transfer/model/validation'

type AmountValues = { amount: string; reference: string }

type AmountStepProps = {
  title: string
  accountName: string
  balanceLabel: string
  recipientName: string
  recipientIban: string
  amount: string
  reference: string
  onBack: () => void
  onContinue: (values: AmountValues) => void
}

type Errors = { amount: string | null; reference: string | null }

export const AmountStep = ({
  title,
  accountName,
  balanceLabel,
  recipientName,
  recipientIban,
  amount: initialAmount,
  reference: initialReference,
  onBack,
  onContinue,
}: AmountStepProps) => {
  const { t } = useTranslation()
  const [amount, setAmount] = useState(initialAmount)
  const [reference, setReference] = useState(initialReference)
  const [errors, setErrors] = useState<Errors>({ amount: null, reference: null })

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const next: Errors = {
      amount: validateAmount(amount),
      reference: validateReference(reference),
    }
    setErrors(next)
    if (next.amount || next.reference) return
    onContinue({ amount, reference })
  }

  return (
    <Stack direction='vertical' space='48'>
      <StepHeader title={title} onBack={onBack} />
      <form onSubmit={handleSubmit} noValidate>
        <Stack direction='vertical' space='24'>
          <TransferSummaryCard
            accountName={accountName}
            balanceLabel={balanceLabel}
            recipient={{ name: recipientName, iban: recipientIban }}
          />
          <Box
            backgroundColor='neutral'
            borderColor='neutral'
            borderWidth='1'
            borderRadius='8'
            padding='24'
          >
            <Stack direction='vertical' space='32'>
              <AmountField
                label={t('transfer.amount.label')}
                value={amount}
                onChange={value => {
                  setAmount(value)
                  if (errors.amount) setErrors(prev => ({ ...prev, amount: null }))
                }}
                error={errors.amount}
              />
              <ReferenceField
                value={reference}
                onChange={value => {
                  setReference(value)
                  if (errors.reference)
                    setErrors(prev => ({ ...prev, reference: null }))
                }}
                error={errors.reference}
              />
            </Stack>
          </Box>
          <Stack direction='horizontal'>
            <Button type='submit' variant='primary' color='teal'>
              {t('common.continue')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Stack>
  )
}
